import type { ReactNode } from 'react';
import { Fragment } from 'react';

import type { ApiListingLocationType } from 'api/types/ApiListingLocationType';
import type { ApiISODateTimeString } from 'api/types/ApiTypedDate';
import { Box } from 'components/Box/Box';
import { IdealistDayLabel } from 'components/IdealistDays/IdealistDayLabel';
import { ListingTypeLabel } from 'modules/listing/components/ListingTypeLabel/ListingTypeLabel';
import { SearchHitInfoBoxListingLabels } from 'modules/search/components/Hit/Info/ListingLabels/SearchHitInfoBoxListingLabels';
import {
  SearchHitInfoFooterHolder,
  SearchHitInfoHeader,
  SearchHitInfoHeaderHeading,
  SearchHitInfoHeaderOrgName,
  SearchHitInfoHolder,
  SearchHitInfoPipe,
  SearchHitInfoStats,
} from 'modules/search/components/Hit/Info/SearchHitInfo.styled';
import { SearchHitApplicationStatus } from 'modules/search/components/Hit/SearchHitApplicationStatus';

type Props = {
  application?: {
    status: 'ACTIVE' | 'INACTIVE';
    created: ApiISODateTimeString;
  };
  endDateTime?: ApiISODateTimeString | null;
  location?: string;
  locationType?: ApiListingLocationType;
  listingStats: ReactNode[];
  name: string;
  orgName?: string | null;
  orgType?: string | null;
  showLabel?: boolean;
  startDateTime?: ApiISODateTimeString | null;
  timezone?: string | null;
  isIdealistDay?: boolean;
};

export function SearchEventHitInfo({
  application,
  endDateTime,
  location,
  locationType,
  listingStats,
  name,
  orgName,
  orgType,
  showLabel,
  startDateTime,
  timezone,
  isIdealistDay,
}: Props) {
  const type = 'EVENT';
  return (
    <SearchHitInfoHolder>
      {showLabel && (
        <div style={{ marginBottom: 14 }}>
          <ListingTypeLabel listingType={type} />
        </div>
      )}

      {isIdealistDay && (
        <Box mb="12px">
          <IdealistDayLabel />
        </Box>
      )}

      <SearchHitInfoHeader>
        <SearchHitInfoHeaderHeading>
          <div>
            <span data-qa-id="search-result-link">{name.slice(0, 120)}</span>
            {name.length > 120 && <>&hellip;</>}
          </div>
        </SearchHitInfoHeaderHeading>

        {(orgType === 'CORPORATION' || orgName) && (
          <SearchHitInfoHeaderOrgName>
            <Box display="flex" alignItems="center" flexWrap="wrap">
              {orgName}
            </Box>
          </SearchHitInfoHeaderOrgName>
        )}
      </SearchHitInfoHeader>

      <SearchHitInfoBoxListingLabels
        listingType={type}
        location={location}
        locationType={locationType}
        timezone={timezone}
        startDateTime={startDateTime}
        endDateTime={endDateTime}
      />

      <SearchHitInfoFooterHolder>
        {(listingStats.length > 0 || application) && (
          <SearchHitInfoStats>
            {listingStats.map((info, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Fragment key={index}>
                {index > 0 && <SearchHitInfoPipe />}
                {info}
              </Fragment>
            ))}
          </SearchHitInfoStats>
        )}

        {application && (
          <SearchHitApplicationStatus
            status={application.status}
            created={application.created}
          />
        )}
      </SearchHitInfoFooterHolder>
    </SearchHitInfoHolder>
  );
}
